import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, ChevronRight, Clock3, Crown, Gem, HelpCircle, Loader2, ShieldAlert, Users } from 'lucide-react'
import toast from 'react-hot-toast'
import HelpModal from '../../../components/HelpModal'
import { useTeamStore } from '../store/useTeamStore'
import TeamAvatar from '../components/TeamAvatar'
import { modeIcon, modeLabel, clampPct } from '../utils/teamStats'
import { CARD_CLASS } from '../utils/teamUi'
import useAppStore from '../../../store/useAppStore'
import { hapticImpact } from '../../../utils/haptic'

const number = (value) => Number(value || 0).toLocaleString()

export const captureStatus = (status, t) => {
  const labels = {
    control: t('team.capture.control'),
    pressure: t('team.capture.pressure'),
    threat: t('team.capture.threat'),
    assault: t('team.capture.assault'),
    contender: t('team.capture.contender'),
    fading: t('team.capture.fading'),
    racing: t('team.capture.racing')
  }
  return labels[status] || ''
}

export const CaptureStatus = ({ status, t }) => status ? (
  <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${status === 'threat' ? 'border-red-500/40 bg-red-500/10 text-red-400' : status === 'assault' ? 'border-amber-400/40 bg-amber-400/10 text-amber-300' : 'border-info/30 bg-info/10 text-info'}`}>
    {captureStatus(status, t)}
  </span>
) : null

export const CapturePrize = ({ payouts, prize, t }) => (
  payouts && prize != null
    ? <span className="inline-flex items-center gap-1 font-bold tabular-nums text-accent-primary"><Gem size={14} />{number(prize)}</span>
    : !payouts ? <span className="text-xs text-text-secondary">{t('team.capture.prizesSoon')}</span> : null
)

export const CaptureProgress = ({ pct, label }) => (
  <div role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={clampPct(pct)} className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/10">
    <div className="h-full rounded-full bg-info transition-[width] motion-reduce:transition-none" style={{ width: `${clampPct(pct)}%` }} />
  </div>
)

export const CaptureRow = ({ entry, t, isMine = false, showEnergy = true, showPlace = true }) => {
  const navigate = useNavigate()
  const slug = entry?.team?.slug
  return (
    <div className={`rounded-xl border p-3 ${isMine ? 'border-2 border-team-accent bg-team-accent/10' : 'border-white/10 bg-dark-primary'}`}>
      <div className="flex items-center gap-3">
        {entry.place != null && <span className={`w-5 shrink-0 text-center text-xs font-bold tabular-nums text-text-secondary ${showPlace ? '' : 'hidden'}`}>{entry.place}</span>}
        <button type="button" disabled={!slug} onClick={() => { hapticImpact('light'); navigate(`/teams/${slug}`) }} className="flex min-w-0 flex-1 items-center gap-2 text-left enabled:active:scale-[0.98] disabled:cursor-default">
          <TeamAvatar team={entry.team} size={36} />
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold text-white">{entry.team?.name || t('team.myTeam')}</span>
            {Number.isFinite(entry.level) && <span className="text-[11px] text-text-secondary">LVL {entry.level}</span>}
          </span>
        </button>
        {entry.is_owner && <Crown size={16} className="shrink-0 text-yellow-400" />}
        {showEnergy && <span className="shrink-0 text-sm font-bold tabular-nums text-white">{number(entry.energy)}</span>}
      </div>
      <CaptureProgress pct={entry.pct} label={entry.team?.name || t('team.myTeam')} />
      <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-text-secondary">
        <CaptureStatus status={entry.status} t={t} />
        <span>{t('team.capture.accrued', { amount: number(entry.accrued) })}</span>
      </div>
    </div>
  )
}

export const useCaptureWindow = (data, reload) => {
  const [remaining, setRemaining] = useState(0)
  const boundaryRef = useRef(null)
  useEffect(() => {
    if (!data?.window) return undefined
    const deadline = data.receivedAt + data.window.ends_in * 1000
    const windowId = Math.round((data.receivedAt - data.window.start_ago * 1000) / (data.window.length * 1000))
    const tick = () => {
      const left = Math.max(0, Math.ceil((deadline - Date.now()) / 1000))
      setRemaining(left)
      if (left === 0 && boundaryRef.current !== windowId) {
        boundaryRef.current = windowId
        reload()
      }
    }
    tick()
    const timer = setInterval(tick, 1000)
    return () => clearInterval(timer)
  }, [data, reload])
  useEffect(() => {
    const timer = setInterval(() => { if (document.visibilityState === 'visible') reload() }, 25000)
    const onVisible = () => { if (document.visibilityState === 'visible') reload() }
    document.addEventListener('visibilitychange', onVisible)
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', onVisible) }
  }, [reload])
  const minutes = String(Math.floor(remaining / 60)).padStart(2, '0')
  const seconds = String(remaining % 60).padStart(2, '0')
  return `${minutes}:${seconds}`
}

export const CaptureEvent = ({ mode, event, window, receivedAt, t }) => {
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    if (!event || !window || !receivedAt) {
      setVisible(false)
      return
    }
    const windowId = Math.round((receivedAt - window.start_ago * 1000) / (window.length * 1000))
    const key = `teamCaptureEvent:${mode}`
    try {
      if (sessionStorage.getItem(key) === String(windowId)) {
        setVisible(false)
        return
      }
      sessionStorage.setItem(key, String(windowId))
    } catch { return }
    setVisible(true)
    const message = t(event === 'captured' ? 'team.capture.captured' : 'team.capture.lost', { mode: modeLabel(mode, t) })
    if (event === 'captured') toast.success(message)
    else toast(message)
  }, [event, mode, window, receivedAt, t])
  return visible && event ? <p className="animate-fadeUp motion-reduce:animate-none rounded-xl border border-team-accent/40 bg-team-accent/10 p-3 text-sm font-semibold text-white">{t(event === 'captured' ? 'team.capture.captured' : 'team.capture.lost', { mode: modeLabel(mode, t) })}</p> : null
}

const TeamCapture = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { capture, isCaptureLoading, captureError, loadCapture } = useTeamStore()
  const myTeam = useAppStore((state) => state.user?.team)
  const isGuest = useAppStore((state) => state.isGuest)
  const minesEnabled = useAppStore((state) => state.user?.mines_enabled !== false)
  const openLogin = useAppStore((state) => state.openLogin)
  const [isHelpOpen, setIsHelpOpen] = useState(false)
  const clock = useCaptureWindow(capture, loadCapture)

  useEffect(() => { loadCapture() }, [loadCapture, isGuest])

  return (
    <div className="mx-auto w-full space-y-4 px-4 py-6 lg:max-w-4xl lg:px-6 lg:py-8">
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => navigate('/teams')} className="rounded-xl bg-surface p-2 text-white active:scale-[0.98]" aria-label={t('common.back')}><ArrowLeft size={20} /></button>
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <h1 className="truncate text-2xl text-white">{t('team.capture.title')}</h1>
          {capture?.enabled && <button type="button" onClick={() => { hapticImpact('light'); setIsHelpOpen(true) }} className="shrink-0 rounded-full bg-dark-secondary/50 p-1.5 text-text-secondary transition-colors hover:bg-dark-secondary active:scale-[0.98]" aria-label={t('team.capture.help.button')}><HelpCircle size={20} /></button>}
        </div>
        <ShieldAlert size={22} className="shrink-0 text-team-accent" />
      </div>
      {isCaptureLoading && !capture ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-accent-primary" /></div> : captureError ? (
        <div className={`${CARD_CLASS} p-6 text-center text-sm text-text-secondary`}>{t(captureError === 'mode_disabled' ? 'team.capture.disabled' : 'team.capture.loadError')}<button type="button" onClick={loadCapture} className="mt-3 block w-full text-accent-primary active:scale-[0.98]">{t('publicProfile.retry')}</button></div>
      ) : capture?.enabled === false ? <div className={`${CARD_CLASS} p-6 text-center text-text-secondary`}>{t('team.capture.disabled')}</div> : capture?.enabled ? <>
        <div className={`${CARD_CLASS} flex flex-wrap items-center justify-between gap-2 p-4 text-sm`}>
          <span className="flex items-center gap-2 text-text-secondary"><Clock3 size={16} />{t('team.capture.nextWindow')}</span>
          <span className="font-bold tabular-nums text-white">{clock}</span>
        </div>
        {!capture.payouts && <p className="rounded-xl border border-info/30 bg-info/10 p-3 text-sm text-info">{t('team.capture.prizesSoon')}</p>}
        {!myTeam && (isGuest ? <button type="button" onClick={openLogin} className={`${CARD_CLASS} w-full p-4 text-left text-sm text-accent-primary`}>{t('guest.login')}</button> : <p className={`${CARD_CLASS} p-4 text-sm text-text-secondary`}>{t('team.capture.noTeam')}</p>)}
        <p className="text-xs text-text-secondary">{t('team.capture.hint')}</p>
        <div className="grid gap-3 lg:grid-cols-2">
          {capture.modes?.filter((item) => item.mode !== 'mines' || minesEnabled).map((item) => {
            const Icon = modeIcon(item.mode)
            return <div key={item.mode} className={`${CARD_CLASS} p-4`}>
              <CaptureEvent mode={item.mode} event={item.my?.event} window={capture.window} receivedAt={capture.receivedAt} t={t} />
              <button type="button" onClick={() => { hapticImpact('light'); navigate(`/teams/capture/${item.mode}`) }} className="flex w-full items-center gap-3 text-left active:scale-[0.98]">
                <span className="rounded-xl border border-team-accent/30 bg-team-accent/10 p-2.5 text-team-accent"><Icon size={22} /></span>
                <span className="min-w-0 flex-1"><span className="block font-bold text-white">{modeLabel(item.mode, t)}</span><span className="flex items-center gap-1 text-xs text-text-secondary"><Users size={12} />{number(item.teams_count)}</span></span>
                <ChevronRight size={18} className="text-text-secondary" />
              </button>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs"><span className="text-text-secondary">{t('team.capture.windowPrize')}</span>{item.owner ? <CapturePrize payouts={capture.payouts} prize={item.prize} t={t} /> : <span className="text-text-secondary">{t('team.capture.free')}</span>}</div>
              <div className="mt-3 space-y-2">
                {item.owner ? <CaptureRow entry={{ ...item.owner, is_owner: true, place: item.my?.is_owner ? item.my.place : undefined }} t={t} isMine={item.my?.is_owner} showPlace={false} /> : <p className="rounded-xl border border-white/10 p-3 text-xs text-text-secondary">{t('team.capture.free')}</p>}
                {item.contenders?.map((entry) => <CaptureRow key={entry.team?.slug || entry.place} entry={entry} t={t} isMine={myTeam?.id === entry.team?.slug} showPlace={false} />)}
                {item.my && !item.my.is_owner && !item.contenders?.some((entry) => entry.team?.slug === myTeam?.id) && <div className="rounded-xl border border-team-accent/40 bg-team-accent/10 p-3 text-xs text-white">{t('team.capture.myPlace', { place: item.my.place ?? '—', energy: number(item.my.energy) })} <CaptureStatus status={item.my.status} t={t} /></div>}
              </div>
            </div>
          })}
        </div>
      </> : null}
      <HelpModal
        isOpen={isHelpOpen && capture?.enabled === true}
        onClose={() => setIsHelpOpen(false)}
        title={t('team.capture.help.title')}
        steps={[
          { title: t('team.capture.help.energyTitle'), description: t('team.capture.help.energyText') },
          { title: t('team.capture.help.teamSizeTitle'), description: t('team.capture.help.teamSizeText') },
          { title: t('team.capture.help.ownerTitle'), description: t('team.capture.help.ownerText') },
          { title: t('team.capture.help.decayTitle'), description: t('team.capture.help.decayText') },
          capture?.payouts
            ? { title: t('team.capture.help.prizeTitle'), description: t('team.capture.help.prizeText') }
            : { title: t('team.capture.help.noPayoutsTitle'), description: t('team.capture.help.noPayoutsText') }
        ]}
        highlightLastStep={false}
      />
    </div>
  )
}

export default TeamCapture
